import { countryTz, restaurantTz } from '@/lib/hours';
import { localeTag } from '@/lib/localeTag';

/** DF-0273 — « Demain » est ambigu exactement au moment où le client s'en sert :
 *  passée à 01:09, une commande « pour demain » désigne le lendemain civil, pas
 *  la fin de la nuit en cours. Ces libellés nomment le jour (« lun. 21/09 ») là
 *  où il est choisi et là où il est rappelé.
 *
 *  Le jour civil se compte dans le fuseau du RESTAURANT, pas dans celui du
 *  navigateur : un client en voyage ne doit pas lire une autre date que la
 *  cuisine. Le nom du jour vient d'`Intl` dans la langue courante — aucune
 *  table de jours codée en dur.
 */

function shortDayFormatter(lng: string, tz: string): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(localeTag(lng), {
    timeZone: tz,
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  });
}

/** DF-0330 — le restaurant (fuseau résolu par le hub, pays en repli) ou, pour
 *  les anciens appelants, son seul pays. */
type Place = { timezone?: string | null; country?: string | null } | string | null | undefined;

function placeTz(place: Place): string {
  return place && typeof place === 'object' ? restaurantTz(place) : countryTz(place);
}

/** Jour civil (YYYY-MM-DD) d'un instant, vu du restaurant. */
function ymdInTz(at: Date, tz: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(at);
}

/** Libellé court du jour situé `offsetDays` après `now` (0 = aujourd'hui,
 *  1 = demain), compté dans le fuseau du restaurant. Ex. « lun. 21/09 ». */
export function dayLabelForOffset(
  now: Date,
  offsetDays: number,
  lng: string,
  place?: Place,
): string {
  if (Number.isNaN(now.getTime())) return '';
  const tz = placeTz(place);
  // Midi UTC : le décalage du jour se fait sur une date nue, à l'abri de tout
  // changement d'heure — puis on la met en forme telle quelle.
  const at = new Date(`${ymdInTz(now, tz)}T12:00:00Z`);
  at.setUTCDate(at.getUTCDate() + offsetDays);
  return shortDayFormatter(lng, 'UTC').format(at);
}

/** SC-1090 — nom complet du jour (« Jeudi ») situé `offsetDays` après `now`,
 *  même calcul que `dayLabelForOffset`. Titre des onglets J+2..J+N, qui portent
 *  comme Aujourd'hui/Demain un nom sur la 1re ligne et la date sur la 2de. */
export function weekdayNameForOffset(
  now: Date,
  offsetDays: number,
  lng: string,
  place?: Place,
): string {
  if (Number.isNaN(now.getTime())) return '';
  const tz = placeTz(place);
  const at = new Date(`${ymdInTz(now, tz)}T12:00:00Z`);
  at.setUTCDate(at.getUTCDate() + offsetDays);
  const name = new Intl.DateTimeFormat(localeTag(lng), { timeZone: 'UTC', weekday: 'long' }).format(at);
  return name.charAt(0).toUpperCase() + name.slice(1);
}

/** Libellé court du jour d'un créneau (ISO), ou `null` si ce créneau tombe le
 *  jour même — auquel cas l'heure seule suffit et le dire alourdirait. */
export function scheduledDayLabel(
  iso: string,
  now: Date,
  lng: string,
  place?: Place,
): string | null {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const tz = placeTz(place);
  if (ymdInTz(at, tz) === ymdInTz(now, tz)) return null;
  return shortDayFormatter(lng, tz).format(at);
}
